import { useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "../ui/icon";
import { NumberField } from "../ui/number_field";
import { Term } from "../ui/term";
import { sumLine } from "./breakdown";
import { COINS, type Coin, consolidate } from "./coins";
import { ACTION_ROW, Button, TextButton } from "./controls";
import { useInventoryEdits } from "./edits";
import { termInfo } from "./glossary";
import { interpolate, type Labels } from "./labels";
import { Bar } from "./pop";
import type { InventoryView, SheetView } from "./sheet_view";

const DOTS: Record<Coin, string> = { gp: "bg-dourado-400", sp: "bg-cinza-400", cp: "bg-dourado-700" };

// A coin's mark, as the purse draws it; the item form's cost labels borrow it.
export function CoinDot({ coin }: { coin: Coin }) {
	return <span aria-hidden="true" class={`h-3.5 w-3.5 flex-none rounded-full border-2 border-black/25 ${DOTS[coin]}`} />;
}
const COIN_INPUT = "cs-num h-9 w-[4.5rem] rounded-lg border border-line bg-surface-raised px-2 text-right text-lg text-ink";

// The old sheet's Converter, asking inside the band where it asked in a browser dialog. Nothing to ask when the purse
// already holds the fewest pieces.
function Converter({ view, labels }: { view: InventoryView; labels: Labels }) {
	const edits = useInventoryEdits();
	const [asking, setAsking] = useState(false);
	const { sheet } = view;
	const purse = { gp: sheet.money_gp, sp: sheet.money_sp, cp: sheet.money_cp };
	const target = consolidate(purse);
	const same = COINS.every((coin) => target[coin] === purse[coin]);
	const showing = asking && !same;
	const confirm = useRef<HTMLButtonElement>(null);
	const converter = useRef<HTMLButtonElement>(null);
	const wasShowing = useRef(false);
	// A purse that became consolidated meanwhile closes the question, so it cannot reopen by itself.
	useEffect(() => {
		if (asking && same) setAsking(false);
	}, [asking, same]);
	// Focus follows the confirm: onto its button when it opens, back to Converter when it is cancelled.
	useEffect(() => {
		if (showing) confirm.current?.focus();
		else if (wasShowing.current) converter.current?.focus();
		wasShowing.current = showing;
	}, [showing]);
	if (!edits) return null;
	if (showing) {
		return (
			<span class={`cs-purse-confirm ${ACTION_ROW} text-sm`}>
				<b>{interpolate(labels.inventory.convert_confirm, { gold: target.gp, silver: target.sp, copper: target.cp })}</b>
				<Button
					tone="primary"
					icon="exchange"
					buttonRef={confirm}
					label={labels.inventory.convert}
					onClick={() => {
						setAsking(false);
						edits.convertCoins();
					}}
				/>
				<TextButton label={labels.edit.cancel} onClick={() => setAsking(false)} />
			</span>
		);
	}
	return <Button tone="alt" icon="exchange" size="sm" buttonRef={converter} disabled={same} label={labels.inventory.convert} onClick={() => setAsking(true)} />;
}

// "Carga", opening both sums; the campaign has none to open, so its column head is the word alone.
export function LoadTerm({ view, labels }: { view: Pick<InventoryView, "derived">; labels: Labels }) {
	const { derived } = view;
	return (
		<Term
			label={labels.inventory.load}
			{...termInfo(labels, "load", labels.inventory.load)}
			lines={
				derived && [
					`${labels.inventory.load_current}: ${sumLine(derived.current_load, labels)}`,
					`${labels.inventory.load_max}: ${sumLine(derived.max_load, labels)}`,
				]
			}
		/>
	);
}

function LoadBar({ derived, labels, busy }: { derived: SheetView["derived"]; labels: Labels; busy: boolean }) {
	const load = derived.current_load.total;
	const max = derived.max_load.total;
	return (
		<span class={`cs-purse-load flex min-w-56 flex-1 items-center gap-2 text-[13px] transition-opacity ${busy ? "opacity-50" : ""}`} aria-busy={busy}>
			<LoadTerm view={{ derived }} labels={labels} />
			<Bar percent={max > 0 ? (load / max) * 100 : 100} color={load > max ? "bg-carmesim-600" : "bg-dourado-400"} label={labels.inventory.load} />
			<b>{load}</b>
			<span class="text-ink-muted">/ {max}</span>
		</span>
	);
}

// The coins, typed (PO, PP, PC), the Converter and the load. `loadBusy`: a change is still on its way, so the load shown is the old one.
export function Purse({ view, labels, loadBusy }: { view: InventoryView; labels: Labels; loadBusy: boolean }) {
	const { sheet, derived } = view;
	const edits = useInventoryEdits();
	const coins: { key: Coin; term: string; abbr: string; value: number }[] = [
		{ key: "gp", term: "po", abbr: labels.inventory.gold, value: sheet.money_gp },
		{ key: "sp", term: "pp", abbr: labels.inventory.silver, value: sheet.money_sp },
		{ key: "cp", term: "pc", abbr: labels.inventory.copper, value: sheet.money_cp },
	];
	return (
		<div class="cs-purse mt-1 rounded-lg bg-surface-sunken px-3 py-2">
			<div class="cs-purse-band">
				<b class="cs-purse-title flex items-center gap-1.5 text-sm">
					<Icon name="coins" class="h-[18px] w-[18px] text-dourado-500" />
					{labels.inventory.coins}
				</b>
				<div class="cs-purse-coins">
					{coins.map((coin) => {
						const info = termInfo(labels, coin.term, coin.abbr);
						return (
							<span key={coin.key} class="cs-coin">
								<span class="flex items-center gap-1.5">
									<CoinDot coin={coin.key} />
									<Term label={coin.abbr} class="text-[13px]" {...info} />
								</span>
								{edits ? (
									<NumberField
										value={coin.value}
										min={0}
										label={info.title}
										class={COIN_INPUT}
										onCommit={(next, from) => edits.setCoin(coin.key, next, from)}
									/>
								) : (
									<span class="cs-num text-xl">{coin.value}</span>
								)}
							</span>
						);
					})}
				</div>
				{edits && (
					<span class="cs-purse-convert">
						<Converter view={view} labels={labels} />
					</span>
				)}
				{derived && <LoadBar derived={derived} labels={labels} busy={loadBusy} />}
			</div>
		</div>
	);
}
